import { Displaying, Spacing } from "@bonusx-global/bx-design-system";
import styled from "@emotion/styled";
import Box from "@mui/material/Box";
import { useCallback } from "react";
import { EMPTY_STRING } from "../../../../../constants/commonStrings";
import { ZERO_DIGIT } from "../../../../../constants/env";
import { useSearchModule } from "../../../../hooks/useSearchModule";
import { type Search } from "../../../../interfaces/Search";
import BackIconButton from "../../../common/back-icon-button/back-icon-button";
import { SearchtInput } from "../../../Header/Search/SearchInput";

const SearchModuleContainer = styled(Box)({
  display: Displaying.flex,
  gap: Spacing["1500"],
  width: "100%",
});

const KEYBOARD_ENTER_KEY = "Enter";

function HeaderV2SearchModule(): JSX.Element {
  const { reset, search, searchText, clearSearch, isVisible: isSearchPanelVisible } = useSearchModule();

  const hideSearchModuleCallback = useCallback(
    function hideSearchModule() {
      reset({ visible: false, searchText: EMPTY_STRING });
    },
    [reset]
  );

  const showSearchModuleCallback = useCallback(
    function showSearchModule(currentQuery?: string) {
      const resetOptions: Partial<Search.Input> = { visible: true };
      if (currentQuery) {
        resetOptions.searchText = currentQuery;
      }
      reset(resetOptions);
      window.scrollTo({ top: 0, left: 0 });
    },
    [reset]
  );

  const onSearchInputChangeCallback = useCallback(
    function onSearchInputChange(event: React.ChangeEvent<HTMLInputElement>) {
      if (event.target?.value.length === ZERO_DIGIT) {
        reset({ visible: true, searchText: EMPTY_STRING });
        return;
      }

      search(event.target?.value);

      if (!isSearchPanelVisible) {
        showSearchModuleCallback(event.target?.value);
      }
    },
    [isSearchPanelVisible, reset, search, showSearchModuleCallback]
  );

  const onSearchInputKeyDownCallback = useCallback(
    function onSearchInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
      if (event.key !== KEYBOARD_ENTER_KEY) {
        return;
      }

      if (!isSearchPanelVisible) {
        showSearchModuleCallback(searchText);
      }
    },
    [isSearchPanelVisible, searchText, showSearchModuleCallback]
  );

  const onSearchInputClickCallback = useCallback(
    function onSearchInputClick() {
      if (!isSearchPanelVisible) {
        showSearchModuleCallback(searchText);
      }
    },
    [isSearchPanelVisible, searchText, showSearchModuleCallback]
  );

  return (
    <SearchModuleContainer>
      {isSearchPanelVisible && <BackIconButton onClick={hideSearchModuleCallback} />}

      <SearchtInput
        value={searchText}
        resetInput={clearSearch}
        autoFocus={false}
        onChange={onSearchInputChangeCallback}
        onKeyDown={onSearchInputKeyDownCallback}
        onClick={onSearchInputClickCallback}
      />
    </SearchModuleContainer>
  );
}

export default HeaderV2SearchModule;
